import {
  downloadMemberImportTemplate,
  executeMemberImportFile,
  getMemberImportDiagnostic,
  MemberImportDiagnosticApiError,
  previewMemberImportFile,
  type MemberImportDiagnosticResponse,
} from '@urbanik/api-client';
import {
  Button,
  DataTable,
  EmptyState,
  StatePanel,
  canonicalStates,
  formatDateTime,
  type DataColumn,
} from '@urbanik/ui';
import { Download, FileSpreadsheet, UploadCloud } from 'lucide-react';
import { useEffect, useState } from 'react';
import { apiBase } from './api-base';
import { useOrganization } from './OrganizationContext';
import './member-import-diagnostic.css';

type State = 'loading' | 'success' | 'empty' | 'error' | 'forbidden';
const stateDefinition = (state: State) => canonicalStates.find((item) => item.kind === state)!;
type Sheet = MemberImportDiagnosticResponse['sheets'][number];
const columns: readonly DataColumn<Sheet>[] = [
  { id: 'position', header: '#', accessor: (sheet) => String(sheet.position) },
  {
    id: 'name',
    header: 'Hoja',
    accessor: (sheet) => sheet.name,
    cell: (sheet) => <strong>{sheet.name}</strong>,
  },
  {
    id: 'visibility',
    header: 'Visibilidad',
    accessor: (sheet) =>
      sheet.visibility === 'visible'
        ? 'Visible'
        : sheet.visibility === 'hidden'
          ? 'Oculta'
          : 'Muy oculta',
  },
  { id: 'rowCount', header: 'Filas', accessor: (sheet) => sheet.rowCount.toLocaleString('es-EC') },
  {
    id: 'columnCount',
    header: 'Columnas',
    accessor: (sheet) => sheet.columnCount.toLocaleString('es-EC'),
  },
  {
    id: 'nonEmptyCellCount',
    header: 'Celdas con contenido',
    accessor: (sheet) => sheet.nonEmptyCellCount.toLocaleString('es-EC'),
  },
  {
    id: 'formulaCellCount',
    header: 'Fórmulas',
    accessor: (sheet) => sheet.formulaCellCount.toLocaleString('es-EC'),
  },
  {
    id: 'tables',
    header: 'Tablas',
    accessor: (sheet) => (sheet.tableNames.length ? sheet.tableNames.join(', ') : '—'),
  },
];

function BulkMemberImport({ onComplete }: { onComplete: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Awaited<
    ReturnType<typeof previewMemberImportFile>
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const validate = async (next: File) => {
    setFile(next);
    setPreview(null);
    setMessage('');
    setBusy(true);
    try {
      setPreview(await previewMemberImportFile(apiBase, next));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos validar la plantilla.');
    } finally {
      setBusy(false);
    }
  };
  const execute = async () => {
    if (!file || !preview || preview.error) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await executeMemberImportFile(apiBase, file);
      setMessage(
        `${result.imported} socio(s) aplicados y ${result.preservedForReview} caso(s) enviados a conciliación individual.`,
      );
      setFile(null);
      setPreview(null);
      onComplete();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No pudimos registrar los socios.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="bulk-member-import" aria-labelledby="bulk-import-title">
      <div className="section-title">
        <p className="kicker">Registro masivo</p>
        <h1 id="bulk-import-title">Importar socios desde una plantilla</h1>
        <p>
          Usa la plantilla para una entidad nueva o carga el XLSM histórico. Para COMIVALLE se exige
          la hoja canónica LISTA DE SOCIOS; las dudas pasan a conciliación individual.
        </p>
      </div>
      <div className="bulk-member-import__actions">
        <Button
          variant="secondary"
          icon={<Download aria-hidden="true" />}
          onClick={() =>
            void downloadMemberImportTemplate(apiBase).catch((error) =>
              setMessage(
                error instanceof Error ? error.message : 'No pudimos descargar la plantilla.',
              ),
            )
          }
        >
          Descargar plantilla XLSX
        </Button>
        <label
          className="bulk-member-import__drop"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const next = event.dataTransfer.files[0];
            if (next) void validate(next);
          }}
        >
          <UploadCloud aria-hidden="true" />
          <span>{file?.name ?? 'Arrastra la plantilla o selecciónala'}</span>
          <small>Archivo .xlsx o .xlsm · máximo 20 MB</small>
          <input
            type="file"
            accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
            onChange={(event) => {
              const next = event.target.files?.[0];
              if (next) void validate(next);
            }}
          />
        </label>
      </div>
      {busy ? (
        <p className="bulk-member-import__notice" role="status">
          Procesando la plantilla…
        </p>
      ) : null}
      {message ? (
        <p className="bulk-member-import__notice" role="status">
          {message}
        </p>
      ) : null}
      {preview ? (
        <div className="bulk-member-import__result">
          <FileSpreadsheet aria-hidden="true" />
          <div>
            <strong>{preview.total} filas revisadas</strong>
            <span>
              {preview.valid} aplicables · {preview.warning} para conciliación · {preview.error} con
              errores
            </span>
            {preview.error ? (
              <ul>
                {preview.rows
                  .filter((row) => row.issues.length)
                  .slice(0, 5)
                  .map((row) => (
                    <li key={row.id}>
                      Fila {row.rowNumber}: {row.issues.join(' ')}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
          <Button disabled={busy || preview.error > 0} onClick={() => void execute()}>
            {preview.warning ? 'Aplicar y abrir conciliación' : `Registrar ${preview.valid} socios`}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export function MemberImportDiagnosticView() {
  const organization = useOrganization();
  const historical = !organization || organization.active.code === 'BASELINE_COMIVALLE';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<State>(historical ? 'loading' : 'empty');
  const [diagnostic, setDiagnostic] = useState<MemberImportDiagnosticResponse | null>(null);
  useEffect(() => {
    if (!historical) {
      setDiagnostic(null);
      setState('empty');
      return;
    }
    const controller = new AbortController();
    setState('loading');
    getMemberImportDiagnostic(apiBase, controller.signal)
      .then((result) => {
        setDiagnostic(result);
        setState(result.sheets.length ? 'success' : 'empty');
      })
      .catch((error: unknown) =>
        setState(
          error instanceof MemberImportDiagnosticApiError && error.kind === 'forbidden'
            ? 'forbidden'
            : 'error',
        ),
      );
    return () => controller.abort();
  }, [attempt, historical]);
  return (
    <div className="shell-page import-diagnostic-page">
      <BulkMemberImport onComplete={() => setAttempt((value) => value + 1)} />
      {!historical ? (
        <EmptyState
          title="Esta entidad aún no tiene una migración histórica"
          description="Usa la plantilla anterior para cargar sus primeros socios. Los archivos históricos de COMIVALLE no se comparten con esta entidad."
        />
      ) : state !== 'success' || !diagnostic ? (
        state === 'error' ? (
          <StatePanel
            state={{
              ...stateDefinition('error'),
              message:
                'No pudimos leer la fuente histórica de COMIVALLE. Verifica que el archivo esté disponible en el servidor.',
            }}
            onAction={() => setAttempt((value) => value + 1)}
          />
        ) : (
          <StatePanel state={stateDefinition(state)} />
        )
      ) : (
        <>
          <header className="page-heading">
            <div>
              <p className="kicker">M04.1 · Diagnóstico histórico</p>
              <h2>Fuente histórica de COMIVALLE</h2>
              <p>
                Esta fuente pertenece exclusivamente a COMIVALLE y se muestra en modo de solo
                lectura.
              </p>
            </div>
            <span className="surface-badge">Solo lectura</span>
          </header>
          <section className="diagnostic-file" aria-label="Fuente diagnosticada">
            <div>
              <span>Archivo</span>
              <strong>{diagnostic.fileName}</strong>
              <small>SHA-256 {diagnostic.sha256}</small>
            </div>
            <div>
              <span>Diagnosticado</span>
              <strong>{formatDateTime(diagnostic.diagnosedAt)}</strong>
              <small>
                {(diagnostic.sizeBytes / 1024 / 1024).toLocaleString('es-EC', {
                  maximumFractionDigits: 2,
                })}{' '}
                MB · {diagnostic.extension.toUpperCase()}
              </small>
            </div>
          </section>
          <section className="diagnostic-summary" aria-label="Resumen del libro">
            <article>
              <span>Hojas</span>
              <strong>{diagnostic.sheetCount}</strong>
            </article>
            <article>
              <span>Tablas</span>
              <strong>{diagnostic.tableCount}</strong>
            </article>
            <article>
              <span>Hojas vacías</span>
              <strong>{diagnostic.emptySheetCount}</strong>
            </article>
          </section>
          <DataTable
            columns={columns}
            rows={diagnostic.sheets}
            visibleColumnIds={new Set(columns.map((column) => column.id))}
            sort={{ columnId: 'position', direction: 'ascending' }}
            onSort={() => {}}
            empty={<StatePanel state={stateDefinition('empty')} />}
          />
        </>
      )}
    </div>
  );
}
